import React, { useState } from 'react';
import { Holding, LiquidityEvent, Loan, Settings, Currency, HoldingType } from '../types';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { COUNTRIES, CURRENCIES, getDefaultCurrency } from '../utils/countries';
import { Shield, Sparkles, User, ArrowRight, Wallet, Globe, CheckCircle2 } from 'lucide-react';
export interface OnboardingData {
  partnerNames: [string, string];
  settings: Settings;
  holdings: Holding[];
  liquidityEvents: LiquidityEvent[];
  loans: Loan[];
  isDemo?: boolean;
}
interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void;
}
const generateId = () => Math.random().toString(36).substr(2, 9);
export function OnboardingWizard({
  onComplete
}: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  // Manual Setup State
  const [p1Name, setP1Name] = useState('');
  const [p1Dob, setP1Dob] = useState('');
  const [p2Name, setP2Name] = useState('');
  const [p2Dob, setP2Dob] = useState('');
  const [country, setCountry] = useState('US');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [assetName, setAssetName] = useState('');
  const [assetAmount, setAssetAmount] = useState('');
  const [assetType, setAssetType] = useState<HoldingType>('Liquid');
  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const defaultCurr = getDefaultCurrency(newCountry);
    if (defaultCurr) {
      setCurrency(defaultCurr);
    }
  };
  const handleLoadDemo = () => {
    const now = new Date().toISOString();
    const demoSettings: Settings = {
      baseCurrency: 'EUR',
      baseCountry: 'BE',
      withdrawalStrategy: 'drawdown',
      partnerDOBs: ['1994-03-15', '1992-08-22'],
      drawdownReturnRate: 4,
      monthlyContribution: 2500,
      timeHorizon: 30,
      desiredDrawdown: 6000,
      inflationEnabled: true,
      inflationRate: 2.5
    };
    const demoHoldings: Holding[] = [{
      id: generateId(),
      person: 'Alex',
      name: 'KBC Checking Account',
      amount: 12000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 0.5,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 12000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Alex',
      name: 'IWDA World ETF (Bolero)',
      amount: 78000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 9,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 78000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Belfius Savings Account',
      amount: 22000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 2.5,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 22000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Revolut EUR Account',
      amount: 4800,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 1,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 4800,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Joint',
      name: 'Argenta Emergency Fund',
      amount: 18000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 2,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 18000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Alex',
      name: 'Company RSUs',
      amount: 40000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 12,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      drawdownAvailableYear: 2,
      yieldsAfterDrawdown: false,
      history: [{
        amount: 40000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Bitcoin',
      amount: 10000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 0,
      returnType: 'none',
      capitalGainsTaxRate: 33,
      drawdownAvailableYear: 0,
      yieldsAfterDrawdown: false,
      history: [{
        amount: 10000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Pensioensparen (KBC)',
      amount: 55000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 7,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 8,
      drawdownAvailableYear: 25,
      yieldsAfterDrawdown: true,
      history: [{
        amount: 55000,
        date: now
      }]
    }, {
      id: 'demo-re-1',
      person: 'Joint',
      name: 'Brussels Rental Apartment',
      amount: 320000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 2.5,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      isRealEstate: true,
      purchasePrice: 280000,
      appreciationRate: 2.5,
      rentalIncome: 1400,
      linkedLoanId: 'demo-loan-1',
      linkedEventIds: ['demo-event-1'],
      realEstateStrategy: 'sell',
      plannedSaleYear: 15,
      drawdownAvailableYear: 15,
      yieldsAfterDrawdown: false,
      history: [{
        amount: 320000,
        date: now
      }]
    }];
    const demoLoans: Loan[] = [{
      id: 'demo-loan-1',
      name: 'Brussels Apartment Mortgage',
      person: 'Joint',
      totalAmount: 224000,
      depositAmount: 56000,
      interestRate: 3.5,
      durationMonths: 300,
      startYear: 0,
      currency: 'EUR',
      repaymentType: 'annuity',
      isActive: true,
      createdAt: now
    }];
    const demoEvents: LiquidityEvent[] = [{
      id: 'demo-event-1',
      name: 'Brussels Apartment Rental Income',
      amount: 1400,
      currency: 'EUR',
      type: 'monthly',
      startYear: 0,
      isActive: true
    }, {
      id: generateId(),
      name: 'Alex Year-End Bonus',
      amount: 6000,
      currency: 'EUR',
      type: 'yearly',
      startYear: 0,
      isActive: true
    }, {
      id: generateId(),
      name: 'Jordan Freelance Consulting',
      amount: 450,
      currency: 'EUR',
      type: 'monthly',
      startYear: 0,
      endYear: 5,
      isActive: true
    }];
    onComplete({
      partnerNames: ['Alex', 'Jordan'],
      settings: demoSettings,
      holdings: demoHoldings,
      liquidityEvents: demoEvents,
      loans: demoLoans,
      isDemo: true
    });
  };
  const handleFinishManual = () => {
    const now = new Date().toISOString();
    const manualSettings: Settings = {
      baseCurrency: currency,
      baseCountry: country,
      withdrawalStrategy: 'drawdown',
      partnerDOBs: [p1Dob, p2Dob],
      drawdownReturnRate: 4,
      monthlyContribution: 0,
      timeHorizon: 30,
      desiredDrawdown: 5000,
      inflationEnabled: false,
      inflationRate: 3
    };
    const manualHoldings: Holding[] = [{
      id: generateId(),
      person: p1Name,
      name: assetName,
      amount: Number(assetAmount),
      currency: currency,
      type: assetType,
      country: country,
      createdAt: now,
      isActive: true,
      expectedReturn: 0,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      history: [{
        amount: Number(assetAmount),
        date: now
      }]
    }];
    onComplete({
      partnerNames: [p1Name, p2Name],
      settings: manualSettings,
      holdings: manualHoldings,
      liquidityEvents: [],
      loans: []
    });
  };
  const isStep1Valid = p1Name.trim() !== '' && p2Name.trim() !== '' && p1Dob !== '' && p2Dob !== '';
  const isStep3Valid = assetName.trim() !== '' && assetAmount !== '' && !isNaN(Number(assetAmount));
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-id="element-1083">
      <div className="max-w-2xl w-full" data-id="element-1084">
        {/* Header / Privacy Banner */}
        <div className="text-center mb-8" data-id="element-1085">
          <img src="https://cdn.magicpatterns.com/uploads/sfi3oXipchL5LQrtC4MqUT/pairfolio_logo_2.png" alt="Pairfolio" className="w-16 h-16 object-contain mb-6 mx-auto" data-id="element-1086" />
          <h1 className="text-3xl font-bold text-slate-900 mb-3" data-id="element-1087">
            Welcome to Pairfolio
          </h1>
          <p className="text-slate-500 text-lg max-w-lg mx-auto" data-id="element-1088">
            Plan your financial future together. Let's get your portfolio set
            up.
          </p>

          <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-100" data-id="element-1089">
            <Shield className="w-4 h-4" data-id="element-1090" />
            Privacy First: All data is stored locally in your browser. Nothing
            is sent to our servers.
          </div>
        </div>

        {step === 0 && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1091">
            <Card className="hover:border-blue-300 transition-all cursor-pointer group" onClick={handleLoadDemo} data-id="element-1092">
              <CardContent className="p-8 text-center h-full flex flex-col items-center justify-center" data-id="element-1093">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-1094">
                  <Sparkles className="w-6 h-6" data-id="element-1095" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1096">
                  Explore Demo Data
                </h3>
                <p className="text-slate-500 text-sm mb-6" data-id="element-1097">
                  Populate the app with a realistic portfolio for a couple (Alex
                  & Jordan) to see how the projections and FIRE calculations
                  work.
                </p>
                <Button variant="primary" className="w-full mt-auto" onClick={e => {
              e.stopPropagation();
              handleLoadDemo();
            }} data-id="element-1098">
                  Load Demo Portfolio
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-slate-400 transition-all cursor-pointer group" onClick={() => setStep(1)} data-id="element-1099">
              <CardContent className="p-8 text-center h-full flex flex-col items-center justify-center" data-id="element-1100">
                <div className="w-12 h-12 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-1101">
                  <User className="w-6 h-6" data-id="element-1102" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1103">
                  Start Fresh
                </h3>
                <p className="text-slate-500 text-sm mb-6" data-id="element-1104">
                  Set up your own profile manually. We'll guide you through
                  adding your names, base currency, and your first asset.
                </p>
                <Button variant="secondary" className="w-full mt-auto" onClick={e => {
              e.stopPropagation();
              setStep(1);
            }} data-id="element-1105">
                  Set Up Manually
                </Button>
              </CardContent>
            </Card>
          </div>}

        {step > 0 && <Card className="shadow-xl shadow-slate-200/50 border-slate-200" data-id="element-1106">
            <div className="px-8 pt-8 pb-4 border-b border-slate-100" data-id="element-1107">
              <div className="flex items-center justify-between mb-2" data-id="element-1108">
                <h2 className="text-xl font-bold text-slate-800" data-id="element-1109">
                  {step === 1 && 'Who is planning?'}
                  {step === 2 && 'Where are you based?'}
                  {step === 3 && 'Add your first asset'}
                </h2>
                <span className="text-sm font-medium text-slate-400" data-id="element-1110">
                  Step {step} of 3
                </span>
              </div>
              <div className="flex gap-2 mt-4" data-id="element-1111">
                {[1, 2, 3].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-slate-100'}`} data-id="element-1112" />)}
              </div>
            </div>

            <CardContent className="p-8" data-id="element-1113">
              {step === 1 && <div className="space-y-6" data-id="element-1114">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1115">
                    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1116">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2" data-id="element-1117">
                        <User className="w-4 h-4 text-blue-500" data-id="element-1118" /> Partner 1
                      </h3>
                      <div data-id="element-1119">
                        <Label data-id="element-1120">First Name</Label>
                        <Input placeholder="e.g. Alex" value={p1Name} onChange={e => setP1Name(e.target.value)} autoFocus data-id="element-1121" />
                      </div>
                      <div data-id="element-1122">
                        <Label data-id="element-1123">Date of Birth</Label>
                        <Input type="date" value={p1Dob} onChange={e => setP1Dob(e.target.value)} data-id="element-1124" />
                      </div>
                    </div>

                    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1125">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2" data-id="element-1126">
                        <User className="w-4 h-4 text-indigo-500" data-id="element-1127" /> Partner 2
                      </h3>
                      <div data-id="element-1128">
                        <Label data-id="element-1129">First Name</Label>
                        <Input placeholder="e.g. Jordan" value={p2Name} onChange={e => setP2Name(e.target.value)} data-id="element-1130" />
                      </div>
                      <div data-id="element-1131">
                        <Label data-id="element-1132">Date of Birth</Label>
                        <Input type="date" value={p2Dob} onChange={e => setP2Dob(e.target.value)} data-id="element-1133" />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4" data-id="element-1134">
                    <Button variant="ghost" onClick={() => setStep(0)} data-id="element-1135">
                      Cancel
                    </Button>
                    <Button variant="primary" disabled={!isStep1Valid} onClick={() => setStep(2)} data-id="element-1136">
                      Continue <ArrowRight className="w-4 h-4 ml-2" data-id="element-1137" />
                    </Button>
                  </div>
                </div>}

              {step === 2 && <div className="space-y-6" data-id="element-1138">
                  <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 space-y-6" data-id="element-1139">
                    <div data-id="element-1140">
                      <Label className="flex items-center gap-2 mb-2" data-id="element-1141">
                        <Globe className="w-4 h-4 text-slate-500" data-id="element-1142" /> Primary
                        Country
                      </Label>
                      <Select searchable value={country} onChange={e => handleCountryChange(e.target.value)} options={COUNTRIES.map(c => ({
                  value: c.code,
                  label: `${c.flag} ${c.name}`
                }))} data-id="element-1143" />
                      <p className="text-xs text-slate-500 mt-2" data-id="element-1144">
                        This helps us set sensible defaults for your assets.
                      </p>
                    </div>

                    <div data-id="element-1145">
                      <Label className="flex items-center gap-2 mb-2" data-id="element-1146">
                        <Wallet className="w-4 h-4 text-slate-500" data-id="element-1147" /> Base
                        Currency
                      </Label>
                      <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                  value: c.code,
                  label: `${c.flag} ${c.code} (${c.symbol})`
                }))} data-id="element-1148" />
                      <p className="text-xs text-slate-500 mt-2" data-id="element-1149">
                        All your projections and charts will be displayed in
                        this currency.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4" data-id="element-1150">
                    <Button variant="ghost" onClick={() => setStep(1)} data-id="element-1151">
                      Back
                    </Button>
                    <Button variant="primary" onClick={() => setStep(3)} data-id="element-1152">
                      Continue <ArrowRight className="w-4 h-4 ml-2" data-id="element-1153" />
                    </Button>
                  </div>
                </div>}

              {step === 3 && <div className="space-y-6" data-id="element-1154">
                  <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 space-y-5" data-id="element-1155">
                    <p className="text-sm text-slate-600 mb-2" data-id="element-1156">
                      Let's add your first asset to get the dashboard started.
                      You can add more later.
                    </p>

                    <div data-id="element-1157">
                      <Label data-id="element-1158">Asset Name</Label>
                      <Input placeholder="e.g. Main Checking Account" value={assetName} onChange={e => setAssetName(e.target.value)} autoFocus data-id="element-1159" />
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1160">
                      <div data-id="element-1161">
                        <Label data-id="element-1162">Amount ({currency})</Label>
                        <Input type="number" min="0" placeholder="0.00" value={assetAmount} onChange={e => setAssetAmount(e.target.value)} data-id="element-1163" />
                      </div>
                      <div data-id="element-1164">
                        <Label data-id="element-1165">Asset Type</Label>
                        <Select value={assetType} onChange={e => setAssetType(e.target.value as HoldingType)} options={[{
                    value: 'Liquid',
                    label: '💧 Liquid Cash/Stocks'
                  }, {
                    value: 'Illiquid',
                    label: '🏢 Illiquid Asset'
                  }]} data-id="element-1166" />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4" data-id="element-1167">
                    <Button variant="ghost" onClick={() => setStep(2)} data-id="element-1168">
                      Back
                    </Button>
                    <Button variant="primary" disabled={!isStep3Valid} onClick={handleFinishManual} data-id="element-1169">
                      <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-1170" /> Complete Setup
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </Card>}
      </div>
    </div>;
}